import * as React from "react";
import { Route, RouteComponentProps, withRouter, Switch, Redirect } from "react-router";
import { QuerySearchParameters, SortByQueryOptions, PagingInfo, PagingInfoResponse, RemixQueryOptions } from "bpm-client-api";
import { AudioVideoProps, SearchBox, AudioVideoDefaultProps, SmartClassicProps, WithToast, SongVersion, SortBy, errorToString, ManagedListView, Loader, Box, ListView, DefaultGrid, ListViewHeader, Top10, withToast, resolveCurrentUrl, ContentType, Stats, ViewType, BPMAlbum, ListviewInteractionProps, BPMGenre, BPMCategory } from 'bpm-web-generic';
import { parse } from "query-string";
import { Analytics } from "src/manager/AnalyticsManager";
import { mediaAPI, metaAPI } from "src/api/config";
import { apiAlbumToBPMAlbum } from "src/transformer";

export interface GenericClassicSmartViewProps extends AudioVideoProps, AudioVideoDefaultProps, ListviewInteractionProps, SmartClassicProps, RouteComponentProps, WithToast {
    page: string;
    category?: string
    genre?: string
    songVersion?: SongVersion
    sortBy?: string
}

export interface GenericClassicSmartViewState {
    apiFilter: QuerySearchParameters
    categories: BPMCategory[]
    genres: BPMGenre[]
    songVersions: SongVersion[]
    sortBys: SortBy[]
    keys: string[]
    categorized_albums?: BPMAlbum[][]
    top10: BPMAlbum[]
}

export default class GenericClassicSmartView<P extends GenericClassicSmartViewProps, S extends GenericClassicSmartViewState> extends React.Component<P, S> {

    private queryFilterParams = {}

    protected loadStats(filter: QuerySearchParameters): Promise<Stats> {
        throw new Error('Must be implemented by subclass!')
    }

    protected loadAlbumsForCategory(_: ContentType, filter: QuerySearchParameters, pagingInfo: PagingInfo): Promise<{ data: BPMAlbum[], pagination: PagingInfoResponse }> {
        throw new Error('Must be implemented by subclass!')
    }

    protected loadAlbums(_: ContentType, filter: QuerySearchParameters, pagingInfo: PagingInfo): Promise<{ data: BPMAlbum[], pagination: PagingInfoResponse }> {
        throw new Error('Must be implemented by subclass!')
    }

    protected loadAlbumsPerCategory(): Promise<{ data: BPMAlbum[] }> {
        throw new Error('Must be implemented by subclass!')
    }

    protected getContext(): string {
        throw new Error('Must be implemented by subclass!')
    }

    protected getTitle(): string {
        throw new Error('Must be implemented by subclass!')
    }

    protected hasSmartClassic() {
        return true;
    }

    protected hideListItemNumber() {
        return false;
    }

    constructor(props: P) {
        super(props);
        const apiFilter = this.getAPIFilter()

        this.state = {
            apiFilter,
            categories: [],
            genres: [],
            top10: [],
            keys: [],
            songVersions: [],
            sortBys: Object.keys(SortByQueryOptions).map((key, index) => {
                return {
                    id: SortByQueryOptions[key],
                    name: GenericClassicSmartView.translateSortByDisplayValue(SortByQueryOptions[key])
                    //Alternativ name: key.toString()
                }
            })
        } as any
        if (this.props.location.state && this.props.location.state.genre) {
            this.state.apiFilter.sub_genre = this.props.location.state.genre
        }
        this.loadAlbumsForCategory = this.loadAlbumsForCategory.bind(this)
        this.loadAlbums = this.loadAlbums.bind(this);
        this.loadStats = this.loadStats.bind(this);
    }

    componentDidMount() {
        if (!(this.props.category)) {
            this.getAlbumsPerCategory()
        }
        this.loadCategories()
        this.loadGenres()
        this.loadTop10()
        this.loadSongVersions()
        metaAPI.getKeys().then(({ data: keys }) => {
            this.setState({ keys })
        })
        if (this.props.genre) {
            Analytics.trackView('genre', this.props.genre)
        } else if (this.props.category) {
            Analytics.trackView('category', this.props.category)
        }
    }

    private getSearchFilter() {
        let filter: any = {

        }
        Object.keys(this.queryFilterParams).forEach(key => {
            if (this.state && this.state.apiFilter[key]) {
                filter[key] = undefined
                if (key == 'bpm') {
                    filter['bpm_min'] = undefined;
                    filter['bpm_max'] = undefined;
                }
            }
        })
        if (this.props.location.search) {
            try {
                filter = Object.assign({}, filter, parse(this.props.location.search))
                this.queryFilterParams = parse(this.props.location.search);
                if (filter.bpm) {
                    filter.bpm_min = filter.bpm;
                    filter.bpm_max = filter.bpm;
                }

                return filter || {}
            } catch {

            }

        }
        return {}
    }

    componentDidUpdate(prevProps: P) {
        if (prevProps.contentType != this.props.contentType) {
            this.setState({ categorized_albums: undefined, categories: [], genres: [] }, () => {
                this.getAlbumsPerCategory()
                this.loadCategories()
                this.loadGenres()
                this.loadTop10()
            })
        } else if (prevProps.category != this.props.category) {
            this.setState({ genres: [], apiFilter: this.getAPIFilter() }, () => {
                this.loadGenres()
            })
            Analytics.trackView('category', this.props.category)
        } else if (prevProps.genre != this.props.genre) {
            this.setState({ apiFilter: this.getAPIFilter() })
            Analytics.trackView('genre', this.props.genre)
        }
        if (prevProps.location.search != this.props.location.search) {
            this.setState({ apiFilter: this.getAPIFilter() }, () => {
                if (this.showCategorizedAlbumView()) {
                    this.setState({ categorized_albums: undefined }, () => this.getAlbumsPerCategory())
                }
            })
        }
    }

    public static translateSortByDisplayValue(key: string) {
        switch (key) {
            case 'date_asc':
                return 'Date ascending'
            case 'date_des':
                return 'Date descending'
            case 'artist_asc':
                return 'Artist ascending'
            case 'artisc_desc':
                return 'Artist descending'
            case 'artist_desc':
                return 'Artist descending'
            case 'title_asc':
                return 'Title ascending'
            case 'title_desc':
                return 'Title descending'
            case 'bpm_asc':
                return 'BPM ascending'
            case 'bpm_desc':
                return 'BPM descending'
            case 'song_version_asc':
                return 'Song Version ascending'
            case 'song_version_desc':
                return 'Song Version descending'
        }
        return key
    }

    protected getAPIFilter(): QuerySearchParameters {
        const searchFilter = this.getSearchFilter()
        const filter = Object.assign({}, (this.state ? this.state.apiFilter : (this.props.location.state || {})), { category: this.props.category, sub_genre: this.props.genre }, searchFilter)
        return filter
    }

    protected showPager() {
        return true
    }

    protected getAlbumsPerCategory() {
        this.loadAlbumsPerCategory().then(({ data: albums }) => {
            let categores = {}
            albums.forEach(album => {
                if (!categores[album.category.id]) {
                    categores[album.category.id] = new Array()
                }
                categores[album.category.id].push(album)
            })
            let categoryArray = new Array<BPMAlbum[]>()
            Object.keys(categores).forEach(key => {
                categoryArray.push(categores[key])
            })
            categoryArray.sort((album1, album2) => {
                return album1[0].category.order! - album2[0].category.order!
            })
            this.setState({ categorized_albums: categoryArray })
        }).catch(err => {
            errorToString(err).forEach(error => {
                this.props.showToast('danger', error)
            }, () => {
                this.getAlbumsPerCategory()
            })
        })
    }

    private loadCategories() {
        metaAPI.getCategories('music').then(({ data: categories }) => {
            this.setState({ categories }, () => {
                if (this.props.category && !this.state.categories.find(c => c.slug == this.props.category)) {
                    this.navigateToCategory()
                }
            })
        }).catch(err => {
            errorToString(err).forEach(mes => {
                this.props.showToast('danger', mes, () => this.loadCategories())
            })
        })
    }

    private loadSongVersions() {
        metaAPI.getVersions('music').then(({ data: songVersions }) => {
            this.setState({
                songVersions: songVersions
            })
        }).catch(err => {
            errorToString(err).forEach(mes => {
                this.props.showToast('danger', mes, () => this.loadSongVersions())
            })
        })
    }

    private loadGenres() {
        if (this.props.category) {
            metaAPI.getGenres('music').then(({ data: genres }) => {
                this.setState({ genres }, () => {
                    if (this.props.genre && !this.state.genres.find(c => c.slug == this.props.genre)) {
                        this.navigateToGenre()
                    }
                })
            }).catch(err => {
                errorToString(err).forEach(mes => {
                    this.props.showToast('danger', mes, () => this.loadGenres())
                })
            })
        } else {
            metaAPI.getGenres('music').then(({ data: genres }) => {
                this.setState({ genres }, () => {
                    if (this.props.genre && !this.state.genres.find(c => c.slug == this.props.genre)) {
                        this.navigateToGenre()
                    }
                })
            }).catch(err => {
                errorToString(err).forEach(mes => {
                    this.props.showToast('danger', mes, () => this.loadGenres())
                })
            })
        }
    }

    private loadTop10() {
        mediaAPI.getAlbumTrending(10, 0, undefined, 'music').then(({ data: albums }) => {
            this.setState({
                top10: albums.map(apiAlbumToBPMAlbum)
            })
        })
    }

    private navigateToCategory(slug?: string) {
        if (slug) {
            this.props.history.push(`/${this.props.page}/${this.props.contentType}/${encodeURIComponent(slug)}${location.search}`)
        } else {
            this.props.history.push(`/${this.props.page}${location.search}`)
        }
    }

    private navigateToGenre(slug?: string) {
        if (slug && this.props.category) {
            this.props.history.push(`/${this.props.page}/${this.props.contentType}/${encodeURIComponent(this.props.category as string)}/${encodeURIComponent(slug)}${location.search}`)
        } else if (slug) {
            this.props.history.push(`/${this.props.page}/${this.props.contentType}/genre/${encodeURIComponent(slug)}${location.search}`)
        } else {
            this.navigateToCategory(this.props.category)
        }
    }

    private showAlbumsForCategoryView() {
        return this.props.category && this.hasSmartClassic()
    }

    private showClassicView() {
        return !this.showAlbumsForCategoryView() && (this.props.viewType == 'classic' || !this.hasSmartClassic())
    }

    protected showCategorizedAlbumView() {
        return !this.showAlbumsForCategoryView() && !this.showClassicView()
    }

    protected getGenre() {
        return this.state.genres.find(g => g.slug == this.props.genre)
    }
    protected getCategory() {
        return this.state.categories.find(g => g.slug == this.props.category)
    }

    protected submitFilter(bpmInput1?: number, bpmInput2?: number, songVersion?: SongVersion, sortBy?: SortBy, key?: string, hideRemix?: boolean) {
        let apiFilter = Object.assign({}, this.state.apiFilter)
        if (bpmInput1 != undefined && bpmInput2 && parseInt(bpmInput1 as any) <= parseInt(bpmInput2 as any)) {
            apiFilter['bpm_min'] = bpmInput1
            apiFilter['bpm_max'] = bpmInput2
        } else {
            apiFilter['bpm_min'] = undefined
            apiFilter['bpm_max'] = undefined
        }
        apiFilter['show_only'] = songVersion
        apiFilter['sort_by'] = (sortBy ? (sortBy.id as SortByQueryOptions) : undefined)
        apiFilter['key'] = key;
        if (hideRemix) {
            apiFilter['remix'] = RemixQueryOptions.Hide
        } else {
            apiFilter['remix'] = RemixQueryOptions.All
        }
        this.props.location.state = apiFilter
        this.setState({ apiFilter }, () => {
            if (this.showCategorizedAlbumView()) {
                this.setState({ categorized_albums: undefined }, () => this.getAlbumsPerCategory())
            }
        })
    }

    public renderContent() {
        if (this.showAlbumsForCategoryView()) {
            return <ManagedListView {...this.props} context={this.getContext()} fluid disablePaging={!this.showPager()} title={(albums) => {
                if (albums.length > 0) {
                    return albums[0].category.name
                }
                return 'Loading…'
            }} apiFunc={this.loadAlbumsForCategory} noAlbumsText={'No Tracks found for the selected criteria'}
                contentType={this.props.contentType} apiFilter={this.state.apiFilter}
                hide={{ number: this.hideListItemNumber() }} />
        } else if (this.showClassicView()) {
            return <ManagedListView {...this.props} context={this.getContext()} fluid disablePaging={!this.showPager()} title={this.getTitle()} apiFunc={this.loadAlbums}
                noAlbumsText={'No Tracks found for the selected criteria'}
                contentType={this.props.contentType} apiFilter={this.state.apiFilter}
                hide={{ number: this.hideListItemNumber() }} />
        }

        if (!this.state.categorized_albums) {
            return <Loader />
        }
        if (this.state.categorized_albums.length == 0) {
            return <Box>
                <p style={{ textAlign: 'center' }}>{'No Tracks found for the selected criteria'}</p>
            </Box>;
        }
        return this.state.categorized_albums.map(category => {
            return <ListView {...this.props} context={this.getContext()} style={{ paddingBottom: '40px' }} key={category[0].category.id} fluid title={category[0].category.name} contentType={this.props.contentType} albums={category}
                hide={{ viewAll: false, number: this.hideListItemNumber() }}
                onViewMoreClick={this.navigateToCategory.bind(this, category[0].category.slug)} />
        })
    }

    protected renderGenreHeader() {
        if (this.getGenre()) {
            return <>
                <p className="link dark" onClick={() => {
                    this.props.history.push('/genres')
                }}>❮ Return to Genres</p>
                <h3>Genre: {this.getGenre()?.name}</h3>
            </>
        }
        return null
    }

    protected renderListViewHeader() {
        return <ListViewHeader
            contentType={this.props.contentType} onContentTypeChange={this.props.onContentTypeChange}
            viewType={this.props.viewType} onViewTypeChange={this.props.onViewTypeChange}
            extraInfoLeft={this.renderGenreHeader()}
        />
    }

    private getSpecialTypesSelected() {
        let selected = new Array()
        if (this.getAPIFilter().serato !== undefined) {
            selected.push('serato')
        }
        if (this.getAPIFilter()['tr-sync'] !== undefined) {
            selected.push('tr')
        }
        return selected
    }

    public render() {
        return (
            <DefaultGrid>
                <div>
                    {this.renderListViewHeader()}
                    {this.renderContent()}
                </div>
                <div>
                    <SearchBox style={{ marginBottom: '20px' }}
                        genres={this.state.genres} genre={this.getGenre()} keys={this.state.keys}
                        hideRemixes={this.state.apiFilter.remix == RemixQueryOptions.Hide}
                        categories={this.state.categories} category={this.getCategory()}
                        onGenreChange={(value) => this.navigateToGenre(value ? value.slug : undefined)}
                        onCategoryChange={(value) => this.navigateToCategory(value ? value.slug : undefined)}
                        songVersions={this.state.songVersions} sortBys={this.state.sortBys}
                        onSubmitPress={(bpmInput1, bpmInput2, songVersion, sortBy, key, remix) => this.submitFilter(bpmInput1, bpmInput2, songVersion, sortBy, key, remix)}
                        bpm_max={this.state.apiFilter.bpm_max} bpm_min={this.state.apiFilter.bpm_min} selectedKey={this.state.apiFilter.key}
                        sortBy={this.state.sortBys.find(s => s.id == this.state.apiFilter.sort_by)}
                        songVersion={this.state.songVersions.find(s => s == this.state.apiFilter.show_only)} />

                    <Top10 style={{ marginBottom: '20px' }} context="Top10" albums={this.state.top10} onAlbumClick={(album) => {
                        this.props.history.push('/share/' + album.id)
                    }} />
                </div>
            </DefaultGrid>

        )
    }
}

export function GenericClassicSmartViewHOC<P extends GenericClassicSmartViewProps>(Component: React.ComponentClass<P>) {
    const InnerComponent = withToast(withRouter(Component) as any) as unknown as React.ComponentClass<GenericClassicSmartViewProps>
    return withRouter(class GenericClassicSmartView extends React.Component<P & RouteComponentProps<{ page: string }>> {
        render() {
            return <Switch>
                <Route path={resolveCurrentUrl(this.props.match.url, '/:viewType(smart|classic)')} render={(viewTypeProps: RouteComponentProps<{ viewType: ViewType }>) => {
                    return <Switch>
                        <Route path={resolveCurrentUrl(viewTypeProps.match.url, '/:contentType(audio|video)')} render={(props: RouteComponentProps<{ contentType: ContentType }>) => {
                            return <InnerComponent {...this.props} page={this.props.match.params.page} viewType={viewTypeProps.match.params.viewType} onViewTypeChange={(type) => {
                                this.props.history.push('/' + this.props.match.params.page + '/' + type + '/' + props.match.params.contentType + location.search)
                            }}
                                contentType={'audio'} onContentTypeChange={(type) => {
                                    sessionStorage['lastContentType'] = type
                                    viewTypeProps.history.push(type)
                                }} />
                        }} />
                        <Route>
                            <Redirect to={resolveCurrentUrl(viewTypeProps.match.url, '/' + (sessionStorage['lastContentType'] || 'audio'))} />
                        </Route>
                    </Switch>
                }} />
                <Route path={resolveCurrentUrl(this.props.match.url, '/:contentType(audio|video)/genre/:genre?')} render={(props: RouteComponentProps<{ contentType: ContentType, category: string, genre?: string }>) => {
                    return <InnerComponent {...this.props} page={this.props.match.params.page}
                        contentType={'audio'} onContentTypeChange={(type) => {
                            sessionStorage['lastContentType'] = type
                            this.props.history.push('/' + this.props.match.params.page + '/' + type + '/genre' + ('/' + props.match.params.genre) + location.search)
                        }}
                        viewType={'classic'}
                        genre={props.match.params.genre ? decodeURIComponent(props.match.params.genre) : undefined} />
                }} />
                <Route path={resolveCurrentUrl(this.props.match.url, '/:contentType(audio|video)/:category/:genre?')} render={(props: RouteComponentProps<{ contentType: ContentType, category: string, genre?: string }>) => {
                    return <InnerComponent {...this.props} page={this.props.match.params.page}
                        contentType={'audio'} onContentTypeChange={(type) => {
                            sessionStorage['lastContentType'] = type
                            this.props.history.push('/' + this.props.match.params.page + '/' + type + '/' + props.match.params.category + (props.match.params.genre ? ('/' + props.match.params.genre) : '') + location.search)
                        }}
                        category={decodeURIComponent(props.match.params.category)} genre={props.match.params.genre ? decodeURIComponent(props.match.params.genre) : undefined} />
                }} />
                <Route>
                    <Redirect to={resolveCurrentUrl(this.props.match.url, '/smart')} />
                </Route>
            </Switch>
        }
    })
}


// WEBPACK FOOTER //
// ./src/pages/GenericClassicSmartView.tsx